/**
 * Language keyword loader and aggregator
 */
import { ACTUALLY_SUPPORTED_LANGUAGES } from '@splitfiesta/shared';

import { deKeywords } from './de';
import { enKeywords } from './en';
import { esKeywords } from './es';
import { frKeywords } from './fr';
import { ptKeywords } from './pt';
import { brandKeywords } from './shared/brands';
import type { BrandKeywords } from './shared/brands';
import type {
  LanguageConfig,
  LanguageKeywords,
  SubcategoryKeywords,
  SupportedLanguage,
} from './types';

// Language configurations
const languageConfigs: Record<SupportedLanguage, LanguageConfig> = {
  en: {
    code: 'en',
    name: 'English',
    keywords: enKeywords,
  },
  es: {
    code: 'es',
    name: 'Spanish',
    keywords: esKeywords,
  },
  fr: {
    code: 'fr',
    name: 'French',
    keywords: frKeywords,
  },
  de: {
    code: 'de',
    name: 'German',
    keywords: deKeywords,
  },
  pt: {
    code: 'pt',
    name: 'Portuguese',
    keywords: ptKeywords,
  },
};

/**
 * Get keywords for a specific language
 */
export function getLanguageKeywords(
  language: SupportedLanguage
): LanguageKeywords {
  return languageConfigs[language].keywords;
}

/**
 * Get all supported languages (centralized from i18n config).
 *
 * Falls back to the static 5-language set when `@splitfiesta/shared`
 * doesn't expose the constant — happens in test envs that mock the
 * module without including `ACTUALLY_SUPPORTED_LANGUAGES`.
 * Without this guard, importing anything from `@splitfiesta/core`
 * (which eagerly merges language keywords at module load) crashes
 * every test that mocks the constant's module for unrelated reasons.
 */
const FALLBACK_LANGUAGES: SupportedLanguage[] = ['en', 'fr', 'es', 'de', 'pt'];
export function getSupportedLanguages(): SupportedLanguage[] {
  if (
    !ACTUALLY_SUPPORTED_LANGUAGES ||
    typeof (ACTUALLY_SUPPORTED_LANGUAGES as unknown as Iterable<unknown>)[
      Symbol.iterator
    ] !== 'function'
  ) {
    return FALLBACK_LANGUAGES;
  }
  return [...ACTUALLY_SUPPORTED_LANGUAGES] as SupportedLanguage[];
}

/**
 * Merge keywords from all languages for a specific category.
 * The extractor may return `undefined` (subcategory keyword maps
 * are now Partial-style — a language file doesn't have to populate
 * every subcategory key) — in that case we skip and move on.
 */
export function mergeKeywordsFromAllLanguages(
  extractor: (keywords: LanguageKeywords) => string[] | undefined
): string[] {
  const allKeywords = new Set<string>();

  for (const lang of getSupportedLanguages()) {
    const keywords = extractor(getLanguageKeywords(lang));
    if (!keywords) continue;
    keywords.forEach(keyword => allKeywords.add(keyword.toLowerCase()));
  }

  return Array.from(allKeywords);
}

/**
 * Merge subcategory keywords from all languages. Subcategory
 * keyword maps now allow `string[] | undefined` per-key (a
 * language doesn't have to populate every subcategory); we skip
 * undefined entries while merging.
 */
export function mergeSubcategoryKeywords<T extends SubcategoryKeywords>(
  extractor: (keywords: LanguageKeywords) => T
): T {
  const mergedKeywords: Record<string, string[]> = {};

  for (const lang of getSupportedLanguages()) {
    const subcategoryKeywords = extractor(getLanguageKeywords(lang));

    for (const [subcategory, keywords] of Object.entries(subcategoryKeywords)) {
      if (!keywords) continue;
      const existing = mergedKeywords[subcategory] ?? [];
      const existingSet = new Set(existing.map(k => k.toLowerCase()));
      for (const keyword of keywords) {
        const lowerKeyword = keyword.toLowerCase();
        if (!existingSet.has(lowerKeyword)) {
          existing.push(keyword);
          existingSet.add(lowerKeyword);
        }
      }
      mergedKeywords[subcategory] = existing;
    }
  }

  return mergedKeywords as T;
}

/**
 * Get transport subcategory keywords from all languages. Includes the
 * shared brand keywords (Carrefour, Uber, Shell, etc.) so the legacy
 * no-locale detector still matches brand names after the migration
 * moved them out of the per-language files. Commons are still merged
 * across all 5 languages — locale-aware filtering lives in
 * `getLocaleAwareSubcategoryKeywords`.
 */
export function getAllTransportSubcategoryKeywords() {
  const merged = mergeSubcategoryKeywords(
    keywords => keywords.transportSubcategories
  );
  return mergeBrandsInto(merged, brandKeywords.transportSubcategories);
}

/**
 * Add the shared brand keywords (deduped, case-insensitive) into an
 * already-merged subcategory map. Used by the legacy
 * `getAllXSubcategoryKeywords()` helpers so they see brands too.
 */
function mergeBrandsInto<T extends SubcategoryKeywords>(
  merged: T,
  brands: Partial<T>
): T {
  const out = merged as Record<string, string[]>;
  for (const [sub, kws] of Object.entries(brands)) {
    if (!kws) continue;
    const existing = out[sub] ?? (out[sub] = []);
    const existingSet = new Set(existing.map(k => k.toLowerCase()));
    for (const kw of kws) {
      const lower = kw.toLowerCase();
      if (!existingSet.has(lower)) {
        existing.push(kw);
        existingSet.add(lower);
      }
    }
  }
  return out as T;
}

/**
 * Get food subcategory keywords from all languages. Includes the
 * shared brand keywords so the legacy no-locale path still matches
 * brand names after migration moved them out of per-language files.
 */
export function getAllFoodSubcategoryKeywords() {
  const merged = mergeSubcategoryKeywords(
    keywords => keywords.foodSubcategories
  );
  return mergeBrandsInto(merged, brandKeywords.foodSubcategories);
}

/**
 * Get drinks subcategory keywords from all languages, plus the shared
 * brand keywords (Heineken, Starbucks, Red Bull, …).
 */
export function getAllDrinksSubcategoryKeywords() {
  const merged = mergeSubcategoryKeywords(
    keywords => keywords.drinksSubcategories
  );
  return mergeBrandsInto(merged, brandKeywords.drinksSubcategories);
}

/**
 * Get context keywords for a specific type from all languages
 */
export function getContextKeywords(
  type: 'flight' | 'fuel' | 'transportTickets'
): string[] {
  const allKeywords = new Set<string>();

  for (const lang of getSupportedLanguages()) {
    const keywords = getLanguageKeywords(lang).context[type];
    keywords.forEach(keyword => allKeywords.add(keyword.toLowerCase()));
  }

  return Array.from(allKeywords);
}

/**
 * Locale-aware variant of `getContextKeywords`. Transport context
 * vocabulary is per-language ('trip/road trip/journey' in EN,
 * 'viaje/trayecto' in ES, 'voyage/trajet' in FR, 'viagem/jornada' in
 * PT, 'tanken/sprit' in DE) — merging globally cross-contaminates
 * locales. Returns only the user's language's context, or null when
 * the locale can't be resolved.
 */
export function getLocaleAwareContextKeywords(
  type: 'flight' | 'fuel' | 'transportTickets',
  locale: string | undefined | null
): string[] | null {
  const langCode = resolveLocaleToLanguage(locale);
  if (!langCode) return null;
  const keywords = getLanguageKeywords(langCode).context[type];
  return keywords.map(k => k.toLowerCase());
}

/**
 * Get food subcategory priorities (uses English as default)
 */
export function getFoodSubcategoryPriority(): string[] {
  // Use English priorities as the default since subcategory names are typically in English
  return getLanguageKeywords('en').foodConfig.subcategoryPriority;
}

/**
 * Get drinks subcategory priorities (uses English as default)
 */
export function getDrinksSubcategoryPriority(): string[] {
  return getLanguageKeywords('en').drinksConfig.subcategoryPriority;
}

/**
 * Get transport subcategory priorities (uses English as default)
 */
export function getTransportSubcategoryPriority(): string[] {
  // Use English priorities as the default since subcategory names are typically in English
  return getLanguageKeywords('en').transportConfig.subcategoryPriority;
}

/**
 * Get entertainment subcategory keywords from all languages. Includes
 * shared brand keywords so the legacy no-locale path still matches
 * Netflix/Spotify/PlayStation/Crave/IMAX/etc. after migration moved
 * them out of per-language files.
 */
export function getAllEntertainmentSubcategoryKeywords() {
  const merged = mergeSubcategoryKeywords(
    keywords => keywords.entertainmentSubcategories
  );
  return mergeBrandsInto(merged, brandKeywords.entertainmentSubcategories);
}

/**
 * Get shopping subcategory keywords from all languages. Includes
 * shared brand keywords so the legacy no-locale path matches
 * Walmart/Carrefour/Zara/Sephora/Home Depot/etc. after migration.
 */
export function getAllShoppingSubcategoryKeywords() {
  const merged = mergeSubcategoryKeywords(
    keywords => keywords.shoppingSubcategories
  );
  return mergeBrandsInto(merged, brandKeywords.shoppingSubcategories);
}

/**
 * Get accommodation subcategory keywords from all languages. Includes
 * shared brand keywords so the legacy no-locale path matches Marriott/
 * Hilton/Airbnb/Booking/etc. after migration.
 */
export function getAllAccommodationSubcategoryKeywords() {
  const merged = mergeSubcategoryKeywords(
    keywords => keywords.accommodationSubcategories
  );
  return mergeBrandsInto(merged, brandKeywords.accommodationSubcategories);
}

/**
 * Get healthcare subcategory keywords from all languages. Includes
 * shared brand keywords so the legacy no-locale path matches CVS/
 * Walgreens/Shoppers/Boots/Cruz Verde/Doc Morris/Unimed/AOK/etc.
 */
export function getAllHealthcareSubcategoryKeywords() {
  const merged = mergeSubcategoryKeywords(
    keywords => keywords.healthcareSubcategories
  );
  return mergeBrandsInto(merged, brandKeywords.healthcareSubcategories);
}

/**
 * Get utilities subcategory keywords from all languages. Includes shared
 * brand keywords so the legacy no-locale path matches Hydro-Québec /
 * Comcast / Verizon / Rogers / EDF / Engie / EnBW / Vivo / Telmex /
 * Sabesp / IPTU / Netflix / etc.
 */
export function getAllUtilitiesSubcategoryKeywords() {
  const merged = mergeSubcategoryKeywords(
    keywords => keywords.utilitiesSubcategories
  );
  return mergeBrandsInto(merged, brandKeywords.utilitiesSubcategories);
}

/**
 * Get entertainment subcategory priorities (hardcoded based on common usage)
 */
export function getEntertainmentSubcategoryPriority(): string[] {
  return [
    'water',
    'outdoor',
    'sports',
    'photography',
    'music',
    'movies',
    'podcasts',
    'events',
    'gaming',
  ];
}

/**
 * Get shopping subcategory priorities (hardcoded based on common usage)
 */
export function getShoppingSubcategoryPriority(): string[] {
  return [
    'services',
    'market',
    'department',
    'toys',
    'electronics',
    'online',
    'groceries',
    'clothes',
    'shoes',
    'hardware',
    'sports',
    'garden',
  ];
}

/**
 * Get accommodation subcategory priorities (hardcoded based on common usage)
 */
export function getAccommodationSubcategoryPriority(): string[] {
  return ['hotel', 'airbnb', 'rent', 'apartment'];
}

/**
 * Get healthcare subcategory priorities (hardcoded based on common usage)
 */
export function getHealthcareSubcategoryPriority(): string[] {
  return ['doctor', 'pharmacy', 'hospital', 'dental'];
}

/**
 * Get utilities subcategory priorities (hardcoded based on common usage)
 */
export function getUtilitiesSubcategoryPriority(): string[] {
  return [
    'streaming',
    'water',
    'electricity',
    'gas',
    'phone',
    'internet',
    'maintenance',
  ];
}

/**
 * Get shopping context keywords from all languages
 */
export function getAllShoppingContextKeywords(subcategory: string): string[] {
  const allKeywords = new Set<string>();

  for (const lang of getSupportedLanguages()) {
    const context = getLanguageKeywords(lang).shoppingContext;
    const keywords = context[subcategory as keyof typeof context];
    if (keywords) {
      keywords.forEach(keyword => allKeywords.add(keyword.toLowerCase()));
    }
  }

  return Array.from(allKeywords);
}

/**
 * Locale-aware variant of `getAllShoppingContextKeywords`. The shopping
 * context vocabulary is per-language ("mode/boutique" for FR, "kleidung/
 * anziehen" for DE, "moda/vestuário" for PT) — merging them globally
 * cross-contaminates locales. This returns only the user's language's
 * context. Returns null when locale doesn't resolve.
 */
export function getLocaleAwareShoppingContextKeywords(
  subcategory: string,
  locale: string | undefined | null
): string[] | null {
  const langCode = resolveLocaleToLanguage(locale);
  if (!langCode) return null;
  const context = getLanguageKeywords(langCode).shoppingContext;
  const keywords = context[subcategory as keyof typeof context];
  return keywords ? keywords.map(k => k.toLowerCase()) : [];
}

/**
 * Get ML pattern keywords for expense categories
 */
export function getExpenseMLPatterns(
  category: string,
  language?: SupportedLanguage
): string[] {
  if (language) {
    const patterns = getLanguageKeywords(language).mlPatterns?.expense;
    return patterns?.[category as keyof typeof patterns] || [];
  }

  // Merge patterns from all languages
  const allPatterns = new Set<string>();

  for (const lang of getSupportedLanguages()) {
    const patterns = getLanguageKeywords(lang).mlPatterns?.expense;
    const categoryPatterns = patterns?.[category as keyof typeof patterns];
    if (categoryPatterns) {
      categoryPatterns.forEach(pattern =>
        allPatterns.add(pattern.toLowerCase())
      );
    }
  }

  return Array.from(allPatterns);
}

/**
 * Get ML pattern keywords for group categories
 */
export function getGroupMLPatterns(
  category: string,
  language?: SupportedLanguage
): string[] {
  if (language) {
    const patterns = getLanguageKeywords(language).mlPatterns?.group;
    return patterns?.[category as keyof typeof patterns] || [];
  }

  // Merge patterns from all languages
  const allPatterns = new Set<string>();

  for (const lang of getSupportedLanguages()) {
    const patterns = getLanguageKeywords(lang).mlPatterns?.group;
    const categoryPatterns = patterns?.[category as keyof typeof patterns];
    if (categoryPatterns) {
      categoryPatterns.forEach(pattern =>
        allPatterns.add(pattern.toLowerCase())
      );
    }
  }

  return Array.from(allPatterns);
}

/**
 * Get stemming suffixes for a specific language
 */
export function getStemmingSuffixes(language?: SupportedLanguage): string[] {
  if (language) {
    return getLanguageKeywords(language).stemming.suffixes;
  }

  // Return all suffixes from all languages for fallback
  const allSuffixes = new Set<string>();
  for (const lang of getSupportedLanguages()) {
    const suffixes = getLanguageKeywords(lang).stemming.suffixes;
    suffixes.forEach(suffix => allSuffixes.add(suffix));
  }
  return Array.from(allSuffixes);
}

// ─── Locale-aware keyword merge ────────────────────────────────────────────
//
// The legacy `getAllXSubcategoryKeywords()` helpers above merge keywords
// across all 5 languages into one global map. That made bilingual users
// happy but it also let language-specific common words (e.g. PT's
// 'natural' as a Brazilian sandwich type) collide with other locales.
//
// The locale-aware path below merges differently:
//   1. ALWAYS include brand keywords from `shared/brands.ts` (locale-
//      agnostic — Carrefour means Carrefour everywhere).
//   2. Include common-word keywords ONLY from the user's primary language.
//   3. If we can't resolve a primary language (locale undefined / not in
//      our supported set), return `null` so the keyword tier reports no
//      match and the cloud LLM tier disambiguates.
//
// Brand extraction is incremental: any subcategory whose brand entry is
// still empty in `shared/brands.ts` continues to fall back to merging
// commons from all languages for that subcategory, matching legacy
// behavior. This lets us migrate one category at a time.

/**
 * Resolve a BCP 47 locale tag (e.g. 'es-CO', 'en-CA', 'fr-CA') to the
 * primary language code we support. Returns null when the tag's primary
 * language isn't one of en/es/fr/pt/de — callers should fall back to
 * the cloud tier for those users.
 */
export function resolveLocaleToLanguage(
  locale: string | undefined | null
): SupportedLanguage | null {
  if (!locale) return null;
  const primary = locale.split(/[-_]/)[0]?.toLowerCase();
  if (!primary) return null;
  const supported = getSupportedLanguages();
  return (supported as string[]).includes(primary)
    ? (primary as SupportedLanguage)
    : null;
}

/**
 * Build a locale-aware subcategory keyword map for a single category.
 *
 * Merges brand keywords (always) with common keywords from the user's
 * primary language only. Returns `null` when the locale can't be
 * resolved — caller should skip the keyword tier and rely on cloud.
 *
 * Any subcategory missing from `brandKeywords[<category>]` still gets
 * the legacy "merge across all languages" treatment, so this can be
 * filled incrementally.
 */
export function getLocaleAwareSubcategoryKeywords<
  T extends SubcategoryKeywords,
>(
  brandsExtractor: (b: BrandKeywords) => Partial<T>,
  commonsExtractor: (k: LanguageKeywords) => T,
  locale: string | undefined | null
): T | null {
  const langCode = resolveLocaleToLanguage(locale);
  if (!langCode) return null;

  const brands = brandsExtractor(brandKeywords);
  const commons = commonsExtractor(getLanguageKeywords(langCode));

  const merged: Record<string, string[]> = {};
  const dedupePush = (sub: string, kw: string) => {
    const existing = merged[sub] ?? (merged[sub] = []);
    const lower = kw.toLowerCase();
    if (!existing.some(k => k.toLowerCase() === lower)) existing.push(kw);
  };

  // 1. Brands first (locale-agnostic).
  for (const [sub, kws] of Object.entries(brands)) {
    if (!kws) continue;
    for (const kw of kws) dedupePush(sub, kw);
  }

  // 2. Commons from the user's language ONLY. We deliberately don't
  //    cross-merge from other languages here — that's the whole point
  //    of the locale-aware path. A French user typing 'augenarzt' (DE
  //    for eye doctor) should NOT match vision; cloud LLM picks that
  //    up. Brands cover the universally-recognised lookups; everything
  //    else is bound to the user's language vocabulary.
  for (const [sub, kws] of Object.entries(commons)) {
    if (!kws) continue;
    for (const kw of kws) dedupePush(sub, kw);
  }

  return merged as T;
}

// Export all language configurations for direct access if needed
export { languageConfigs };

// Export types
export type {
  LanguageConfig,
  LanguageKeywords,
  SupportedLanguage,
  TransportKeywords,
  TransportSubcategoryKeywords,
  FoodSubcategoryKeywords,
  DrinksSubcategoryKeywords,
  EntertainmentSubcategoryKeywords,
  ShoppingSubcategoryKeywords,
  AccommodationSubcategoryKeywords,
  HealthcareSubcategoryKeywords,
  UtilitiesSubcategoryKeywords,
  ContextKeywords,
  SubcategoryKeywords,
} from './types';
